/*
 * blogi-artikkeli.js — the ONE script the blog gets.
 *
 * docs/AGENT/editable-sections-standard.md §1 rule 4 says "native HTML first,
 * and no JavaScript", and §4.9 adds "no IntersectionObserver, no polyfill".
 * This file breaks both, once, on purpose: it is the single scoped exception
 * argued in docs/AGENT/blog-build-plan.md §3.7 and recorded in the standard
 * itself. The TOC active state is real navigational feedback on a 2 000-word
 * page, and the CSS-only alternative (view-timeline-name + timeline-scope) is
 * Chromium-only today — a highlight that exists in one browser family is worse
 * than one that exists everywhere.
 *
 * Two jobs, and nothing else:
 *   1. the read-progress bar
 *   2. the table-of-contents scrollspy
 *
 * ⚠️ THE SCROLL CONTAINER IS NOT THE DOCUMENT ABOVE 990px. Horizon's
 * assets/base.css puts `overflow: hidden` on html and body at >=990px and
 * scrolls `.page-wrapper` instead (the "squeeze" layout). So `window.scrollY`
 * is permanently 0 on desktop, a `scroll` listener on `window` never fires,
 * and an IntersectionObserver with the default root watches the wrong box.
 * Horizon ships the answer as @theme/scroll-container; we import it rather
 * than re-deriving it, and that import is why this file is a module.
 * Being a module also means the browser evaluates it exactly ONCE however many
 * sections reference it — which is what lets both artikkeli-otsikko (the bar)
 * and artikkeli-runko (the TOC) load it without a global guard, so hiding
 * either band in the editor cannot break the other.
 *
 * CONSTRAINTS, all non-negotiable:
 *   - it does nothing if its elements are absent;
 *   - every link works with the script blocked — this is decoration over a
 *     page that is already complete;
 *   - no library, no polyfill, no third-party request;
 *   - motion is opt-in (§6.1 item 9): the bar's transition is authored in CSS
 *     inside `@media (prefers-reduced-motion: no-preference)`, so under
 *     `reduce` it jumps rather than eases; its resting appearance is CSS —
 *     this file only writes `transform`, never `width`, so a scroll never
 *     triggers layout;
 *   - CLAUDE.md JS conventions: `globalThis` over `window`, `Number.isFinite`
 *     over hand-rolled checks, `element.remove()`.
 */
import { getScrollContainer, getScrollEventTarget, getIntersectionRoot } from '@theme/scroll-container';

const doc = globalThis.document;

/* ------------------------------------------------------------------ 1. bar */
/* ⚠️ The bar is NOT removed under `prefers-reduced-motion: reduce`, which is a
   reversal of the first version. That preference is about animation, not about
   suppressing information — and the animation is already handled correctly in
   CSS, where the transition is authored inside
   `@media (prefers-reduced-motion: no-preference)`. So under `reduce` the bar
   still tracks the scroll, it just jumps instead of easing. Removing it threw
   away the feature to honour a preference the CSS had already honoured. */
const bar = doc.querySelector('[data-blogi-edistyminen]');
const fill = bar?.querySelector('[data-blogi-edistyminen-palkki]');

if (bar && fill) {
  let ticking = false;

  const paint = () => {
    ticking = false;
    const container = getScrollContainer();
    const scrollable = container.scrollHeight - container.clientHeight;
    let ratio = scrollable > 0 ? container.scrollTop / scrollable : 0;
    if (!Number.isFinite(ratio)) ratio = 0;
    ratio = Math.min(1, Math.max(0, ratio));
    /* `transform` only, never `width`: a width change on every scroll frame
       is a layout on every scroll frame. No aria is written either — the bar
       is `aria-hidden`, see the section that renders it. */
    fill.style.transform = `scaleX(${ratio})`;
  };

  const request = () => {
    if (ticking) return;
    ticking = true;
    globalThis.requestAnimationFrame(paint);
  };

  getScrollEventTarget().addEventListener('scroll', request, { passive: true });
  globalThis.addEventListener('resize', request, { passive: true });
  paint();
}

/* ----------------------------------------------------------- 2. scrollspy */
const nav = doc.querySelector('[data-blogi-sisallys]');
const body = doc.querySelector('[data-blogi-proosa]');

if (nav && body && typeof globalThis.IntersectionObserver === 'function') {
  const byId = new Map();
  const headings = [];

  for (const link of nav.querySelectorAll('a[href^="#"]')) {
    /* A stray `%` in a heading makes decodeURIComponent throw, and an
       uncaught throw here would take the whole scrollspy with it. Fail soft on
       the one link rather than hard on the feature. */
    let id;
    try {
      id = decodeURIComponent(link.getAttribute('href').slice(1));
    } catch {
      id = link.getAttribute('href').slice(1);
    }
    if (!id) continue;
    const heading = doc.getElementById(id);
    if (!heading || !body.contains(heading)) continue;
    byId.set(id, link);
    headings.push(heading);
  }

  if (headings.length > 0) {
    let activeId = null;

    const setActive = (id) => {
      if (id === activeId) return;
      const previous = activeId ? byId.get(activeId) : null;
      if (previous) {
        previous.removeAttribute('aria-current');
        previous.parentElement?.classList.remove('is-active');
      }
      activeId = id;
      const next = activeId ? byId.get(activeId) : null;
      if (next) {
        /* `aria-current="true"` is ours, not the reference's — that one
           toggles classes only and tells a screen reader nothing. */
        next.setAttribute('aria-current', 'true');
        next.parentElement?.classList.add('is-active');
      }
    };

    const seen = new Map();

    const observer = new globalThis.IntersectionObserver(
      (entries) => {
        for (const entry of entries) seen.set(entry.target.id, entry);

        /* The topmost heading currently on screen wins. If none is, keep the
           last one the reader scrolled past — that is the section they are in. */
        let best = null;
        for (const heading of headings) {
          const entry = seen.get(heading.id);
          if (!entry?.isIntersecting) continue;
          if (best === null || entry.boundingClientRect.top < best.boundingClientRect.top) best = entry;
        }

        if (best) {
          setActive(best.target.id);
          return;
        }

        let passed = null;
        const limit = globalThis.innerHeight * 0.4;
        for (const heading of headings) {
          if (heading.getBoundingClientRect().top < limit) passed = heading.id;
        }
        if (passed) setActive(passed);
      },
      {
        /* rootMargin takes px and % only — a rem value is rejected and throws.
           96px is the same header clearance scroll-padding uses. */
        root: getIntersectionRoot(),
        rootMargin: '-96px 0px -55% 0px',
        threshold: 0,
      }
    );

    for (const heading of headings) observer.observe(heading);
  }
}
